import * as BookmarkTypes from 'common/types/reports/bookmark';
import {handleListReorder} from 'common/components/draggable-list/utils';
import {getAlphaLabelFromIndex} from 'common/util';
import {isFormulaShowClause} from 'iron/common/report/insights/models/utils';

import {getMetricAtIndex, updateMetricOrReferencedMetric} from '../helpers';
import {replaceFormulaReferences} from '../formula-utils';
import {BookmarkDragOperationProps} from './types';

export function calculateFormulaReferenceChanges(
  oldEntries: BookmarkTypes.ShowClause[],
  entries: BookmarkTypes.ShowClause[],
) {
  const referenceUpdates: Record<string, string> = {};

  // track any existing entries that changed index
  entries.forEach((entry, newIndex) => {
    const oldIndex = oldEntries.findIndex((target) => target._idx === entry._idx);

    if (oldIndex !== -1 && newIndex !== oldIndex) {
      const oldLabel = getAlphaLabelFromIndex(oldIndex);
      const newLabel = getAlphaLabelFromIndex(newIndex);

      referenceUpdates[oldLabel] = newLabel;
    }
  });

  return referenceUpdates;
}

/**
 * Handles rearranging the global filters section when dragging and dropping filters.
 */
export function handleMetricsRearrange(props: BookmarkDragOperationProps) {
  const entryGenerator = <T extends BookmarkTypes.ShowClause = BookmarkTypes.ShowClause>(entry: T) => {
    return {
      ...entry,
      _idx: undefined,
      isExpanded: true,
      id: null,
      userNamed: false,
      name: undefined,
      isHidden: false,
    } satisfies T;
  };

  props.updateParams((params) => {
    // when parentMetricIndex is defined, it indicates that the drag operation is within a formula metric
    if (props.targetData.parentMetricIndex !== undefined) {
      const targetClause = getMetricAtIndex({
        index: props.targetData.parentMetricIndex,
        params,
        type: BookmarkTypes.TopLevelMetricType.Formula,
      });

      if (!targetClause?.referencedMetrics) {
        return;
      }

      handleListReorder({
        event: props.event,
        entries: targetClause.referencedMetrics,

        entryGenerator,

        setEntries: (updatedEntries, {oldEntries}) => {
          const referenceChanges = calculateFormulaReferenceChanges(oldEntries, updatedEntries);
          targetClause.definition = replaceFormulaReferences(targetClause.definition!, referenceChanges);

          targetClause.referencedMetrics = updatedEntries;

          updateMetricOrReferencedMetric({
            index: props.targetData.parentMetricIndex!,
            params,
            newClause: targetClause,
          });
        },
      });
    } else {
      handleListReorder({
        event: props.event,
        entries: props.params.sections.show!,
        entryGenerator,
        setEntries: (updatedEntries, {oldEntries}) => {
          const referenceChanges = calculateFormulaReferenceChanges(oldEntries, updatedEntries);
          params.sections.show = updatedEntries.map((entry) => {
            if (isFormulaShowClause(entry) && (entry.referencedMetrics?.length ?? 0) === 0 && !entry.id) {
              entry.definition = replaceFormulaReferences(entry.definition!, referenceChanges);
            }

            return entry;
          });
        },
      });
    }
  });
}
